import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Separator } from '@/shared/components/ui/separator';
import type { TaskPriority } from '../../types';

const PRIORITY_OPTIONS: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  urgent: 'text-chart-5',
  high: 'text-chart-3',
  medium: 'text-chart-4',
  low: 'text-chart-2',
};

function PriorityIcon({
  priority,
  className,
}: {
  priority: TaskPriority | 'none';
  className?: string;
}) {
  const colorClass = priority === 'none' ? 'text-muted-foreground' : PRIORITY_COLOR[priority];
  return <Flag className={cn('size-3.5 shrink-0', colorClass, className)} aria-hidden />;
}

interface PriorityPickerProps {
  priority?: TaskPriority;
  onSelect: (priority: TaskPriority | null) => void;
  disabled?: boolean;
}

export function PriorityPicker({ priority, onSelect, disabled }: PriorityPickerProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);

  function handleSelect(p: TaskPriority | null) {
    onSelect(p);
    setOpen(false);
  }

  const allPriorities = priority
    ? PRIORITY_OPTIONS.filter((p) => p !== priority)
    : PRIORITY_OPTIONS;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="tasks-priority-picker-trigger"
          className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
          onClick={(e) => e.stopPropagation()}
        >
          <PriorityIcon priority={priority ?? 'none'} />
          <span className={priority ? PRIORITY_COLOR[priority] : 'text-muted-foreground'}>
            {priority ? t(`priority.${priority}`) : t('priority.none')}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-48 p-0"
        data-testid="tasks-priority-picker-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 text-xs font-semibold">{t('priority.title')}</div>
        <Separator />

        {priority && (
          <>
            <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {t('priority.activeTitle')}
            </div>
            <div data-testid="tasks-priority-picker-active-section" className="px-1 pb-1">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs bg-accent/40 hover:bg-accent"
              >
                <PriorityIcon priority={priority} />
                <span className={PRIORITY_COLOR[priority]}>{t(`priority.${priority}`)}</span>
              </button>
            </div>
            <Separator />
          </>
        )}

        <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {t('priority.allTitle')}
        </div>
        <div data-testid="tasks-priority-picker-all-section" className="px-1 pb-1">
          {allPriorities.map((p) => (
            <button
              type="button"
              key={p}
              data-testid={`tasks-priority-option-${p}`}
              onClick={() => handleSelect(p)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
            >
              <PriorityIcon priority={p} />
              <span className={PRIORITY_COLOR[p]}>{t(`priority.${p}`)}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
